import { PageHeader } from '../components/PageHeader';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useQuery } from '@tanstack/react-query';
import backend from '../services/backend';
import { useParams } from 'react-router-dom';
import { SortableTable } from '../components/SortableTable';
import { Link } from '../components/Link';
import { entityViewTitle, ID } from '../components/ID';
import CopyButton from '../components/CopyButton';
import type { ApiEndpoints } from '@avisoma/shared';
import type { UseQueryResult } from '@tanstack/react-query';
import clsx from 'clsx';
import { DocumentModelUsageSummary, hasModelUsageToDisplay } from '../components/DocumentModelUsageSummary';
import { estimateModelUsageCost, formatUsdEstimate } from '../lib/modelUsageCostEstimate';
import { formatNumber } from '../lib/numberFormat';
import { formatDateTime } from '../lib/dateFormat';

export default function OrganisationView() {
  const { id } = useParams<{ id: string }>();
  const decodedId = id ? decodeURIComponent(id) : '';

  const organisationQuery = useQuery({
    queryKey: ['organisation', decodedId],
    queryFn: () => backend.getOrganisation(decodedId).then((res) => res.data),
    enabled: !!decodedId,
  });

  useDocumentTitle(entityViewTitle(decodedId, organisationQuery.data?.item.name ?? 'Organisation'));

  const mu = organisationQuery.data?.modelUsage;
  const usageSideBySide =
    !!mu &&
    hasModelUsageToDisplay(mu.pdfParser, mu.dataExtraction, mu.embeddings, mu.segmentation, mu.htmlConversionCostUsd);

  return (
    <div>
      <PageHeader
        title={entityViewTitle(decodedId, organisationQuery.data?.item.name ?? 'Organisation')}
        description="Details and linked projects"
        icon={<BuildingOffice2Icon className="size-6" />}
        showBackButton={true}
      />

      <div className="grid grid-cols-1 gap-4">
        <div
          className={clsx(
            'grid grid-cols-1 gap-4 lg:items-start',
            usageSideBySide ? 'lg:grid-cols-2' : 'lg:grid-cols-1',
          )}
        >
          <OrganisationDetails organisationQuery={organisationQuery} />
          {usageSideBySide ? (
            <div className="min-w-0">
              <OrganisationUsage organisationQuery={organisationQuery} />
            </div>
          ) : null}
        </div>
        <LinkedProjects organisationQuery={organisationQuery} />
      </div>
    </div>
  );
}

function OrganisationDetails({
  organisationQuery,
}: {
  organisationQuery: UseQueryResult<ApiEndpoints<string>['/api/organisations/:id']['GET']['response']>;
}) {
  if (organisationQuery.isLoading) {
    return <div className="text-sm text-gray-500 dark:text-zinc-400">Loading…</div>;
  }

  if (organisationQuery.isError) {
    return <div className="text-red-600 dark:text-red-400">Failed to load organisation.</div>;
  }

  const item = organisationQuery.data?.item;
  if (!item) {
    return <div className="text-sm text-gray-500 dark:text-zinc-400">Not found.</div>;
  }

  const data = organisationQuery.data;
  const totalPageCount = data?.modelUsage.totalPageCount ?? 0;
  const uniquePageCount = data?.uniquePageCount ?? 0;
  const projectCount = data?.projects.length ?? 0;

  return (
    <div className="bg-white dark:bg-zinc-800 rounded-lg shadow dark:shadow-zinc-950/30 dark:border dark:border-zinc-700 p-6">
      <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-zinc-100">Organisation</h2>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">ID</dt>
          <dd className="mt-0.5 text-sm text-gray-900 dark:text-zinc-100 flex items-center gap-2 min-w-0">
            <span className="min-w-0">
              <ID id={item.id} />
            </span>
            <CopyButton className="shrink-0" text={item.id} />
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Name</dt>
          <dd className="mt-0.5 text-sm text-gray-900 dark:text-zinc-100">{item.name}</dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Created</dt>
          <dd className="mt-0.5 text-sm text-gray-900 dark:text-zinc-100">{formatDateTime(item.createdAt)}</dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Updated</dt>
          <dd className="mt-0.5 text-sm text-gray-900 dark:text-zinc-100">{formatDateTime(item.updatedAt)}</dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Total pages</dt>
          <dd className="mt-0.5 text-sm text-gray-900 dark:text-zinc-100 tabular-nums">
            {formatNumber(totalPageCount)}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Unique pages</dt>
          <dd className="mt-0.5 text-sm text-gray-900 dark:text-zinc-100 tabular-nums">
            {formatNumber(uniquePageCount)}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Projects</dt>
          <dd className="mt-0.5 text-sm text-gray-900 dark:text-zinc-100 tabular-nums">
            {formatNumber(projectCount)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

function OrganisationUsage({
  organisationQuery,
}: {
  organisationQuery: UseQueryResult<ApiEndpoints<string>['/api/organisations/:id']['GET']['response']>;
}) {
  if (!organisationQuery.data) {
    return null;
  }

  const { modelUsage } = organisationQuery.data;

  return (
    <DocumentModelUsageSummary
      segmentationUsage={modelUsage.segmentation}
      pdfParserUsage={modelUsage.pdfParser}
      dataExtractionUsage={modelUsage.dataExtraction}
      dataExtractionFlashPreviewUsage={modelUsage.dataExtractionFlashPreview}
      embeddingsUsage={modelUsage.embeddings}
      htmlConversionCostUsd={modelUsage.htmlConversionCostUsd}
      pageCount={modelUsage.totalPageCount}
      title="Model API usage (organisation)"
      collapsibleStorageKey="organisationView.collapsible.modelApiUsage"
      secondaryFootnote="Totals sum each linked project’s usage; documents attached to more than one project are counted more than once."
    />
  );
}

function LinkedProjects({
  organisationQuery,
}: {
  organisationQuery: UseQueryResult<ApiEndpoints<string>['/api/organisations/:id']['GET']['response']>;
}) {
  if (!organisationQuery.data) {
    return null;
  }

  const projects = organisationQuery.data.projects;

  if (projects.length === 0) {
    return (
      <div className="bg-white dark:bg-zinc-800 rounded-lg shadow dark:shadow-zinc-950/30 dark:border dark:border-zinc-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">Linked projects</h2>
        <p className="mt-2 text-sm text-gray-500 dark:text-zinc-400">No projects linked yet.</p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-zinc-800 rounded-lg shadow dark:shadow-zinc-950/30 dark:border dark:border-zinc-700 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-zinc-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">Linked projects</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
          Projects associated with this organisation through uploads.
        </p>
      </div>
      <div className="p-2">
        <SortableTable
          limitHeight={false}
          data={projects}
          defaultSortBy="createdAt"
          defaultSortDir="desc"
          storageId="organisationView.linkedProjects"
          columns={[
            {
              accessor: 'id',
              label: 'ID',
              render: (row) => (
                <Link to={`/projects/${row.id}`} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                  <ID id={row.id} />
                </Link>
              ),
            },
            {
              accessor: 'documentCount',
              label: 'Documents',
              render: (row) => row.documentCount,
            },
            {
              accessor: 'uploadCount',
              label: 'Uploads',
              render: (row) => row.uploadCount,
            },
            {
              accessor: 'esn',
              label: 'ESN',
              render: (row) => row.esn ?? '—',
            },
            {
              accessor: 'modelUsage',
              label: 'Est. cost',
              render: (row) => formatUsdEstimate(estimateModelUsageCost(row.modelUsage).total),
            },
            {
              accessor: 'createdAt',
              label: 'Created',
              render: (row) => formatDateTime(row.createdAt),
            },
          ]}
          rowKey={(row) => row.id}
        />
      </div>
    </div>
  );
}
